import { memo } from "react";
import { Calendar, MapPin, Users, ChevronRight } from "lucide-react";

export const GameCard = memo(({ game, onGameClick, onTeamClick }) => {
  const isHome = game.location === "home";
  const opponent = isHome ? game.away_team : game.home_team;
  const opponentTeamId = isHome ? game.away_team_id : game.home_team_id;
  const teamScore = isHome ? game.home_score : game.away_score;
  const oppScore = isHome ? game.away_score : game.home_score;
  const isWin = game.result === "W";
  const isLoss = game.result === "L";

  const date = new Date(game.date);
  const formattedDate = date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
  const year = date.getFullYear();

  return (
    <div
      onClick={() => onGameClick(game)}
      className={`group relative bg-white rounded-xl border-2 transition-all duration-200 cursor-pointer hover:shadow-lg hover:-translate-y-0.5 ${
        isWin
          ? "border-green-200 hover:border-green-300"
          : isLoss
          ? "border-red-200 hover:border-red-300"
          : "border-gray-200 hover:border-gray-300"
      }`}
    >
      <div
        className={`absolute top-0 left-0 w-1.5 h-full rounded-l-xl ${
          isWin ? "bg-green-500" : isLoss ? "bg-red-500" : "bg-gray-400"
        }`}
      />

      <div className="p-4 pl-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
              <Calendar className="w-3.5 h-3.5" />
              <span>
                {formattedDate}, {year}
              </span>
              <span className="text-gray-300">•</span>
              <MapPin className="w-3.5 h-3.5" />
              <span className="capitalize">
                {isHome ? "Home" : "Away"}
                {game.neutral_site && " (Neutral)"}
              </span>
            </div>

            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">
                {isHome ? "vs" : "@"}
              </span>
              {opponentTeamId ? (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onTeamClick(opponentTeamId);
                  }}
                  className="text-base font-semibold text-gray-900 hover:text-blue-600 transition-colors truncate"
                >
                  {opponent}
                </button>
              ) : (
                <span className="text-base font-semibold text-gray-900 truncate">{opponent}</span>
              )}
            </div>

            {game.attendance > 0 && (
              <div className="flex items-center gap-1.5 text-xs text-gray-400 mt-2">
                <Users className="w-3 h-3" />
                <span>{game.attendance.toLocaleString()} attendance</span>
              </div>
            )}
          </div>

          <div className="flex flex-col items-end gap-1">
            <div
              className={`text-2xl font-bold tracking-tight ${
                isWin ? "text-green-600" : isLoss ? "text-red-600" : "text-gray-600"
              }`}
            >
              {teamScore} - {oppScore}
            </div>
            <div
              className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                isWin ? "bg-green-100 text-green-700" : isLoss ? "bg-red-100 text-red-700" : "bg-gray-100 text-gray-600"
              }`}
            >
              {isWin ? "WIN" : isLoss ? "LOSS" : "TIE"}
            </div>
            {game.innings && game.innings !== 9 && (
              <span className="text-xs text-gray-400">{game.innings} innings</span>
            )}
          </div>
        </div>

        <div className="absolute right-3 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
          <ChevronRight className="w-5 h-5 text-gray-400" />
        </div>
      </div>
    </div>
  );
});

GameCard.displayName = "GameCard";
